import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import Note from '@/components/Forms/Note';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { FreelancerWageResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

import FreelancerWageInputs from './FreelancerWageInputs';

export type FormDataT = {
  hour_amount?: string;
  day_amount?: string;
  month_amount?: string;
  job_amount?: string;
  hour_currency?: string;
  day_currency?: string;
  month_currency?: string;
  job_currency?: string;
  hour_wage_changed_at?: string;
  day_wage_changed_at?: string;
  month_wage_changed_at?: string;
  job_wage_changed_at?: string;
  other_wage_changed_at?: string;
  other_amount?: string;
};

const Heading = styled(Text)`
  margin-bottom: 24px;
`;

interface Props {
  onClose: () => void;
  wages?: FreelancerWageResource[];
  departmentId: number;
}

const FreelancerWagesModal: FC<Props> = ({ onClose, departmentId }) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const {
    handleSubmit,
    setError,
    clearErrors,
    watch,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      hour_currency: 'euro',
      day_currency: 'euro',
      month_currency: 'euro',
      job_currency: 'euro',
    },
  });

  const hourAmount = watch('hour_amount');
  const dayAmount = watch('day_amount');
  const monthAmount = watch('month_amount');
  const jobAmount = watch('job_amount');

  const isCreateDisabled =
    !hourAmount &&
    !dayAmount &&
    !monthAmount &&
    !jobAmount &&
    !watch('other_amount');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.post<FormDataT, any>(
          `/v1/payments/freelancers/department/store-wages`,
          {
            freelancer_department_details_id: departmentId,
            ...data,
          },
        );

        await queryClient.invalidateQueries(['freelancers_details'], {
          exact: false,
        });

        alerts.success(`Freelancer's wages added succesfully`);

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, false);
      }
    })(e);
  };

  return (
    <Modal stopPropagation={false}>
      <SidePanel heading="Add wages details" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FreelancerWageInputs type="hour" control={control} />
              <FreelancerWageInputs type="day" control={control} />
              <FreelancerWageInputs type="month" control={control} />
              <FreelancerWageInputs type="job" control={control} />
              <Larger>
                <Heading $type="button">Other</Heading>
                <Note>
                  Add wages to display them on the freelancer's profile. Please
                  note that this text is for informational purposes only and
                  will not be used for automatic calculations
                </Note>
              </Larger>
              <FormField
                control={control}
                name="other_wage_changed_at"
                label="Wage Start / Change"
                type="datepicker"
                datepickerType="date"
              />
              <FormField
                type="textarea"
                control={control}
                name="other_amount"
                placeholder="Add amount"
                label="Other Wages Logs"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting || isCreateDisabled}
              loading={isSubmitting}
            >
              Create
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default FreelancerWagesModal;
